<template>
  <div class="knowledge-graph-system">
    <!-- 背景装饰 -->
    <div class="bg-decoration">
      <div class="bg-circle bg-circle-1"></div>
      <div class="bg-circle bg-circle-2"></div>
      <div class="bg-circle bg-circle-3"></div>
    </div>

    <!-- 浮动元素 -->
    <div class="floating-elements" ref="floatingContainer"></div>

    <!-- 英雄区域 -->
    <section class="hero-section">
      <div class="hero-container">
        <div class="hero-badge">
          <span class="hero-badge-dot"></span>
          <span>职业能力协同图谱智能化管理平台</span>
        </div>
        
        <div class="hero-mascot">
          <div class="mascot-container">
            <div class="mascot-text">KG</div>
          </div>
        </div>
        
        <h1 class="hero-title">职业能力协同图谱系统V1.0</h1>
        <p class="hero-subtitle">建筑工程技术及钢结构专业知识图谱可视化与智能管理平台</p>
      </div>
    </section>

    <!-- 知识图谱可视化 -->
    <section class="graph-section">
      <div class="graph-container">
        <div class="graph-controls">
          <button class="control-btn" title="放大" @click="handleZoom('in')">🔍</button>
          <button class="control-btn" title="缩小" @click="handleZoom('out')">🔎</button>
          <button class="control-btn" title="重置视图" @click="handleReset">🎯</button>
          <button class="control-btn" title="全屏" @click="handleFullscreen">📺</button>
        </div>
        
        <div class="graph-header">
          <h2 class="graph-title">建筑工程技术及钢结构专业知识图谱</h2>
          <p class="graph-subtitle">基于三层知识库架构的专业能力可视化展示与节点主理人管理</p>
        </div>

        <div class="knowledge-graph" ref="graphContainer">
          <!-- 知识节点 -->
          <div 
            v-for="node in graphNodes" 
            :key="`node-${node.id}`"
            :class="['graph-node', node.className]"
            :style="{ top: node.topPercent + '%', left: node.leftPercent + '%' }"
            :title="node.title"
            @click="handleNodeClick(node)"
            @mouseenter="handleNodeHover(node)"
            @mouseleave="handleNodeLeave"
          >
            <div class="node-icon">{{ node.icon }}</div>
            <div>{{ node.title }}</div>
          </div>

          <!-- 连接线 -->
          <div 
            v-for="(conn, index) in connections" 
            :key="`conn-${index}`"
            :class="['graph-connection', `connection-${index + 1}`]"
          ></div>
        </div>
      </div>
    </section>

    <!-- 快速操作 -->
    <section class="quick-actions-section">
      <div class="section-header">
        <h2 class="section-title">
          <span>⚡</span>
          <span>快捷操作入口</span>
        </h2>
        <p class="section-subtitle">快速执行创建、导入、查询等核心任务</p>
      </div>
      <div class="quick-actions-grid">
        <div 
          v-for="(action, index) in quickActions" 
          :key="`action-${index}`"
          class="quick-action-card"
          @click="handleQuickAction(action)"
        >
          <div class="quick-action-icon">{{ action.icon }}</div>
          <div class="quick-action-text">
            <div class="quick-action-title">{{ action.title }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- 三层知识库架构 -->
    <section class="library-section">
      <div class="library-container">
        <div class="section-header">
          <h2 class="section-title">
            <span>📚</span>
            <span>三层知识库架构</span>
          </h2>
          <p class="section-subtitle">公有库、专业库、私有库的模块化管理，支持6位数字编码扩展</p>
        </div>
        <div class="library-grid">
          <div 
            v-for="(library, index) in libraries" 
            :key="`library-${index}`"
            :class="['library-card', { highlight: library.highlight }]"
            @click="handleLibraryClick(library)"
          >
            <div class="library-icon">{{ library.icon }}</div>
            <h3 class="library-title">{{ library.title }}</h3>
            <p class="library-description">{{ library.description }}</p>
            <div class="library-stats">
              <span class="library-size">{{ library.size }}</span>
              <span class="library-encoding">{{ library.encoding }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 节点主理人管理 -->
    <section class="node-manager-section">
      <div class="manager-header">
        <h2 class="manager-title">
          知识图谱节点<br>
          <span class="manager-title-gradient">主理人管理系统</span>
        </h2>
        <p class="manager-subtitle">
          确保知识可靠性的专业认证体系
          <span class="reliability-badge">可靠性保障</span>
        </p>
      </div>

      <div class="stats-container">
        <div class="stats-grid">
          <div v-for="(stat, index) in stats" :key="`stat-${index}`" class="stat-card">
            <div class="stat-number" :ref="stat.ref">{{ stat.value }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>

      <div class="manager-container">
        <div class="manager-grid">
          <div 
            v-for="(manager, index) in managers" 
            :key="`manager-${index}`"
            class="manager-card"
            @click="handleManagerClick(manager)"
          >
            <div class="manager-node-icon">{{ manager.icon }}</div>
            <h3 class="manager-node-title">{{ manager.title }}</h3>
            <p class="manager-name">{{ manager.name }}</p>
            <span class="manager-status">
              <span>✓</span>
              <span>认证专家</span>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- 数据导入与生成功能组件 -->
    <DataImport />

    <!-- 模型信息部分 -->
    <section class="model-section">
      <div class="model-container">
        <div class="model-content">
          <div class="model-info">
            <h2 class="model-info-title">模型和参数信息</h2>
            <div class="info-grid">
              <div v-for="(info, index) in modelInfo" :key="`info-${index}`" class="info-item">
                <div class="info-label">{{ info.label }}</div>
                <div class="info-value">{{ info.value }}</div>
              </div>
            </div>
          </div>
          
          <div class="model-visual">
            <div class="model-diagram">🤖</div>
            <p class="model-desc">
              基于深度学习的建筑工程技术知识图谱构建模型，
              融合NLP、图神经网络等前沿技术，
              为专业知识管理提供智能化支撑。
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- 系统配置与多终端试用组件 -->
    <SystemConfig />
  </div>
</template>

<script>
import DataImport from './components/DataImport.vue'
import SystemConfig from './components/SystemConfig.vue'

export default {
  name: 'KnowledgeGraphHome',
  components: {
    DataImport,
    SystemConfig
  },
  data() {
    return {
      graphNodes: [
        { id: 1, title: '建筑施工技术', icon: '🏗️', className: 'node-construction', topPercent: 36, leftPercent: 12.5 },
        { id: 2, title: '钢结构工程', icon: '⚙️', className: 'node-steel', topPercent: 16, leftPercent: 29.2 },
        { id: 3, title: '建筑材料', icon: '🧱', className: 'node-material', topPercent: 56, leftPercent: 25 },
        { id: 4, title: '结构设计', icon: '📐', className: 'node-design', topPercent: 30, leftPercent: 45.8 },
        { id: 5, title: '安全管理', icon: '🛡️', className: 'node-safety', topPercent: 70, leftPercent: 16.7 },
        { id: 6, title: 'BIM应用', icon: '💻', className: 'node-bim', topPercent: 56, leftPercent: 41.7 },
        { id: 7, title: '质量控制', icon: '✅', className: 'node-quality', topPercent: 20, leftPercent: 62.5 },
        { id: 8, title: '项目管理', icon: '📊', className: 'node-manager', topPercent: 44, leftPercent: 58.3 }
      ],
      connections: [1, 2, 3, 4, 5, 6],
      quickActions: [
        { icon: '➕', title: '创建新知识图谱节点' },
        { icon: '📁', title: '导入建筑工程技术文档' },
        { icon: '🔍', title: '智能搜索专业知识库' },
        { icon: '👥', title: '管理节点主理人权限' }
      ],
      libraries: [
        {
          icon: '🏗️',
          title: '00_公有库（行业标准）',
          description: '涵盖建筑工程技术行业标准、法规政策、技术规范等通用知识，支持跨专业知识查询与应用。',
          size: '5.2GB',
          encoding: '000001-999999',
          highlight: true
        },
        {
          icon: '🎓',
          title: '01_建筑工程技术专业库',
          description: '按课程体系归纳的专业知识库，包含专业基础、核心课程、实训指导等教学资源。',
          size: '3.8GB',
          encoding: '010001-019999'
        },
        {
          icon: '⚙️',
          title: '02_钢结构专业库',
          description: '钢结构设计、施工、检测等专业知识，融合最新技术标准和工程案例。',
          size: '2.1GB',
          encoding: '020001-029999'
        },
        {
          icon: '👤',
          title: '03_用户私有库',
          description: '支持用户自定义内容管理，包括项目案例、技术资料、学习笔记等个性化知识。',
          size: '0.9GB',
          encoding: '030001-039999'
        }
      ],
      stats: [
        { label: '知识节点总数', value: 0, target: 327, ref: 'nodeCount' },
        { label: '认证主理人', value: 0, target: 45, ref: 'managerCount' },
        { label: '节点关联关系', value: 0, target: 856, ref: 'connectionCount' },
        { label: '知识可靠性评分', value: 0, target: 94.7, ref: 'reliabilityScore' }
      ],
      managers: [
        { icon: '🏗️', title: '建筑施工技术', name: '主理人：张建国教授' },
        { icon: '⚙️', title: '钢结构工程', name: '主理人：李钢华工程师' },
        { icon: '🧱', title: '建筑材料', name: '主理人：王材料博士' },
        { icon: '💻', title: 'BIM应用', name: '主理人：陈数字专家' },
        { icon: '🛡️', title: '安全管理', name: '主理人：刘安全高工' },
        { icon: '✅', title: '质量控制', name: '主理人：赵质量监理' }
      ],
      modelInfo: [
        { label: 'AI模型', value: 'ConstructionKG-Pro' },
        { label: '模型版本', value: 'v3.2.1' },
        { label: '知识库规模', value: '12.0GB' },
        { label: '节点数量', value: '50,000+' },
        { label: '关系类型', value: '156种' },
        { label: '准确率', value: '94.7%' },
        { label: '更新频率', value: '实时更新' },
        { label: '支持语言', value: '中英双语' }
      ],
      floatingInterval: null,
      zoomLevel: 1
    }
  },
  mounted() {
    this.createFloatingElements()
    this.animateNumbers()
    this.initializeAnimations()
    this.handleResize()
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    if (this.floatingInterval) {
      clearInterval(this.floatingInterval)
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    createFloatingElements() {
      const container = this.$refs.floatingContainer
      
      const createElement = () => {
        const element = document.createElement('div')
        element.className = 'floating-element'
        element.style.left = Math.random() * 100 + '%'
        element.style.width = element.style.height = Math.random() * 40 + 20 + 'px'
        element.style.animationDelay = Math.random() * 20 + 's'
        element.style.animationDuration = Math.random() * 20 + 20 + 's'
        container.appendChild(element)
        
        setTimeout(() => {
          if (element.parentNode) {
            element.remove()
          }
        }, 40000)
      }
      
      for (let i = 0; i < 8; i++) {
        setTimeout(createElement, i * 1000)
      }
      
      this.floatingInterval = setInterval(createElement, 5000)
    },
    
    animateNumbers() {
      this.stats.forEach(stat => {
        const element = this.$refs[stat.ref]?.[0]
        if (element) {
          let current = 0
          const increment = stat.target / 50
          const timer = setInterval(() => {
            current += increment
            if (current >= stat.target) {
              current = stat.target
              clearInterval(timer)
            }
            if (stat.ref === 'reliabilityScore') {
              element.textContent = current.toFixed(1)
            } else {
              element.textContent = Math.floor(current).toLocaleString()
            }
          }, 30)
        }
      })
    },
    
    initializeAnimations() {
      const animatedElements = this.$el.querySelectorAll(
        '.library-card, .manager-card, .graph-node, .quick-action-card, ' + 
        '.stat-card, .import-feature, .info-item'
      )
      
      animatedElements.forEach((el, index) => {
        el.style.opacity = '0'
        el.style.transform = 'translateY(30px)'
        el.style.transition = `opacity 0.6s ease ${index * 0.05}s, transform 0.6s ease ${index * 0.05}s`
        
        setTimeout(() => {
          el.style.opacity = '1'
          el.style.transform = 'translateY(0)'
        }, 100)
      })

      const cards = this.$el.querySelectorAll('.library-card, .manager-card, .stat-card')
      cards.forEach(card => {
        card.addEventListener('mousemove', (e) => {
          const rect = card.getBoundingClientRect()
          const x = e.clientX - rect.left
          const y = e.clientY - rect.top
          
          const centerX = rect.width / 2
          const centerY = rect.height / 2
          
          const rotateX = (y - centerY) / centerY * -8
          const rotateY = (x - centerX) / centerX * 8
          
          card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) translateY(-4px)`
        })
        
        card.addEventListener('mouseleave', () => {
          card.style.transform = 'perspective(1000px) rotateX(0) rotateY(0) translateY(0)'
        })
      })
    },
    
    handleNodeClick(node) {
      alert(`节点详情：${node.title}\n\n这里将显示该知识节点的详细信息，包括：\n- 相关文档数量\n- 知识点关联关系\n- 主理人信息\n- 最近更新时间`)
    },
    
    handleNodeHover(node) {
      this.$el.querySelectorAll('.graph-connection').forEach(conn => {
        conn.style.opacity = '0.9'
        conn.style.height = '4px'
        conn.style.filter = 'drop-shadow(0 0 12px rgba(46, 92, 230, 0.8))'
      })
    },
    
    handleNodeLeave() {
      this.$el.querySelectorAll('.graph-connection').forEach(conn => {
        conn.style.opacity = '0.6'
        conn.style.height = '3px'
        conn.style.filter = 'none'
      })
    },
    
    handleZoom(direction) {
      if (direction === 'in') {
        this.zoomLevel = Math.min(this.zoomLevel * 1.2, 3)
      } else {
        this.zoomLevel = Math.max(this.zoomLevel / 1.2, 0.5)
      }
      
      const graphContainer = this.$refs.graphContainer
      if (graphContainer) {
        graphContainer.style.transform = `scale(${this.zoomLevel})`
      }
    },
    
    handleReset() {
      this.zoomLevel = 1
      const graphContainer = this.$refs.graphContainer
      if (graphContainer) {
        graphContainer.style.transform = 'scale(1)'
      }
    },
    
    handleFullscreen() {
      const graphContainer = this.$refs.graphContainer?.parentElement
      if (graphContainer?.requestFullscreen) {
        graphContainer.requestFullscreen()
      } else {
        alert('全屏模式功能\n\n图谱将在全屏模式下显示，提供更好的可视化体验。')
      }
    },
    
    handleQuickAction(action) {
      switch(action.title) {
        case '创建新知识图谱节点':
          alert('创建知识图谱节点功能\n\n请输入节点名称、类型和相关描述，系统将自动建立与现有节点的关联关系。')
          break
        case '导入建筑工程技术文档':
          this.$el.querySelector('.upload-zone')?.click()
          break
        case '智能搜索专业知识库':
          const searchQuery = prompt('请输入搜索关键词：')
          if (searchQuery) {
            alert(`正在搜索"${searchQuery}"相关内容...\n\n搜索范围：\n- 公有库（行业标准）\n- 建筑工程技术专业库\n- 钢结构专业库\n- 用户私有库`)
          }
          break
        case '管理节点主理人权限':
          alert('节点主理人管理功能\n\n支持以下操作：\n- 分配/变更主理人\n- 设置权限级别\n- 审核知识内容\n- 管理认证状态')
          break
      }
    },
    
    handleLibraryClick(library) {
      const libraryInfo = {
        '00_公有库（行业标准）': {
          size: '5.2GB',
          documents: '12,847',
          categories: '建筑法规、技术标准、安全规范、质量标准',
          encoding: '000001-999999'
        }
      }
      const info = libraryInfo[library.title] || {}
      alert(`${library.title}详细信息：\n\n数据规模：${info.size || library.size}\n文档数量：${info.documents || 'N/A'}\n主要分类：${info.categories || 'N/A'}\n编码范围：${library.encoding}`)
    },
    
    handleManagerClick(manager) {
      alert(`主理人详细信息：\n\n负责节点：${manager.title}\n${manager.name}\n\n职责范围：\n- 知识内容审核与更新\n- 专业标准制定与维护\n- 学术质量监督与评估\n- 跨库数据关联管理\n\n认证状态：已通过专业认证\n权限级别：高级管理员`)
    },
    
    handleResize() {
      // 在窗口大小改变时重新计算图谱大小
      const graphContainer = this.$refs.graphContainer
      if (graphContainer) {
        // 触发重绘
        graphContainer.style.display = 'none'
        graphContainer.offsetHeight // 触发重排
        graphContainer.style.display = ''
      }
    }
  }
}
</script>

<style scoped>
/* 全局样式重置 */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', 'PingFang SC', 'Microsoft YaHei', sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.knowledge-graph-system {
  background: linear-gradient(135deg, #EBF5FF 0%, #F0F9FF 50%, #FFF7ED 100%);
  color: #111827;
  line-height: 1.6;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}

/* 背景装饰 */
.bg-decoration {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  z-index: -1;
  overflow: hidden;
}

.bg-circle {
  position: absolute;
  border-radius: 50%;
  opacity: 0.03;
}

.bg-circle-1 {
  width: 600px;
  height: 600px;
  background: #2E5CE6;
  top: -300px;
  right: -200px;
}

.bg-circle-2 {
  width: 400px;
  height: 400px;
  background: #FF6B35;
  bottom: -200px;
  left: -100px;
}

.bg-circle-3 {
  width: 300px;
  height: 300px;
  background: #4682B4;
  top: 50%;
  left: 10%;
}

/* 英雄区域 */
.hero-section {
  padding: 40px 0 60px;
  text-align: center;
  position: relative;
}

.hero-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: white;
  padding: 8px 20px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
  color: #2E5CE6;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  margin-bottom: 24px;
}

.hero-badge-dot {
  width: 8px;
  height: 8px;
  background: #10B981;
  border-radius: 50%;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.5; transform: scale(0.8); }
}

.hero-mascot {
  width: 180px;
  height: 180px;
  margin: 0 auto 40px;
  position: relative;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-20px); }
}

.mascot-container {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #E6F3FF 0%, #FFF7ED 50%, #F0F8FF 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 20px 40px rgba(70, 130, 180, 0.2);
  position: relative;
  overflow: hidden;
}

.mascot-container::before {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.8) 0%, transparent 60%);
}

.mascot-text {
  font-size: 80px;
  font-weight: 800;
  background: linear-gradient(135deg, #4682B4 0%, #FF6B35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  z-index: 1;
}

.hero-title {
  font-size: 56px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 24px;
  background: linear-gradient(135deg, #4682B4 0%, #2E5CE6 50%, #FF6B35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: -0.02em;
}

.hero-subtitle {
  font-size: 20px;
  color: #6B7280;
  max-width: 700px;
  margin: 0 auto 40px;
  line-height: 1.6;
}

/* 知识图谱可视化 */
.graph-section {
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;
}

.graph-container {
  background: white;
  border-radius: 32px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 32px;
  position: relative;
  border: 1px solid #E5E7EB;
}

.graph-controls {
  position: absolute;
  top: 32px;
  right: 32px;
  display: flex;
  gap: 8px;
  z-index: 10;
}

.control-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 12px;
  background: white;
  color: #111827;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

.control-btn:hover {
  background: #2E5CE6;
  color: white;
  transform: scale(1.05);
}

.graph-header {
  margin-bottom: 32px;
  padding-bottom: 24px;
  border-bottom: 2px solid #F3F4F6;
}

.graph-title {
  font-size: 28px;
  font-weight: 800;
  background: linear-gradient(135deg, #4682B4 0%, #2E5CE6 50%, #FF6B35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 8px;
}

.graph-subtitle {
  font-size: 16px;
  color: #6B7280;
}

.knowledge-graph {
  position: relative;
  height: 500px;
  background: #F9FAFB;
  border-radius: 16px;
  overflow: hidden;
  border: 2px solid #E5E7EB;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  transform-origin: center center;
}

/* 知识图谱节点 */
.graph-node {
  position: absolute;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  text-align: center;
  line-height: 1.2;
}

.graph-node:hover {
  transform: scale(1.15);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  z-index: 5;
}

.node-icon {
  font-size: 24px;
  margin-bottom: 4px;
}

.node-construction { background: linear-gradient(135deg, #FF6B35 0%, #FF8A50 100%); }
.node-steel { background: linear-gradient(135deg, #4682B4 0%, #5A9BD4 100%); }
.node-material { background: linear-gradient(135deg, #8B5CF6 0%, #A855F7 100%); }
.node-design { background: linear-gradient(135deg, #10B981 0%, #34D399 100%); }
.node-safety { background: linear-gradient(135deg, #EF4444 0%, #F87171 100%); }
.node-bim { background: linear-gradient(135deg, #06B6D4 0%, #22D3EE 100%); }
.node-quality { background: linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%); }
.node-manager { background: linear-gradient(135deg, #EC4899 0%, #F472B6 100%); }

/* 图谱连接线 */
.graph-connection {
  position: absolute;
  height: 3px;
  background: linear-gradient(90deg, #2E5CE6 0%, #FF6B35 100%);
  opacity: 0.6;
  transform-origin: left center;
  box-shadow: 0 0 10px rgba(46, 92, 230, 0.3);
}

.connection-1 { top: 43%; left: 21.5%; width: 8.33%; transform: rotate(-30deg); }
.connection-2 { top: 26%; left: 38.4%; width: 8.33%; transform: rotate(25deg); }
.connection-3 { top: 66%; left: 34.2%; width: 8.33%; transform: rotate(-20deg); }
.connection-4 { top: 40%; left: 55%; width: 6.67%; transform: rotate(-40deg); }
.connection-5 { top: 80%; left: 25.8%; width: 10%; transform: rotate(10deg); }
.connection-6 { top: 36%; left: 55%; width: 7.5%; transform: rotate(30deg); }

/* 快速操作 */
.quick-actions-section {
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;
}

.section-header {
  margin-bottom: 32px;
  text-align: center;
}

.section-title {
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.section-subtitle {
  font-size: 18px;
  color: #6B7280;
  text-align: center;
}

.quick-actions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.quick-action-card {
  background: white;
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: hidden;
}

.quick-action-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #2E5CE6 0%, #FF6B35 100%);
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.quick-action-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border-color: #60A5FA;
}

.quick-action-card:hover::before {
  opacity: 0.05;
}

.quick-action-icon {
  width: 48px;
  height: 48px;
  background: #F3F4F6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
}

.quick-action-text {
  flex: 1;
  position: relative;
  z-index: 1;
}

.quick-action-title {
  font-weight: 600;
  font-size: 16px;
  color: #111827;
  line-height: 1.4;
}

/* 知识库部分 */
.library-section {
  background: white;
  padding: 80px 0;
  position: relative;
  border-top: 1px solid #E5E7EB;
}

.library-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.library-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(360px, 1fr));
  gap: 24px;
  margin-top: 48px;
}

.library-card {
  background: #F9FAFB;
  border-radius: 24px;
  padding: 32px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.library-card::before {
  content: '';
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(135deg, #2E5CE6 0%, #FF6B35 100%);
  border-radius: 24px;
  opacity: 0;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  z-index: -1;
}

.library-card:hover {
  transform: translateY(-4px);
  background: white;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.library-card:hover::before {
  opacity: 1;
}

.library-card.highlight {
  background: linear-gradient(135deg, rgba(70, 130, 180, 0.05) 0%, rgba(255, 107, 53, 0.05) 100%);
  border: 2px solid #4682B4;
}

.library-icon {
  width: 64px;
  height: 64px;
  background: white;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.library-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #111827;
}

.library-description {
  font-size: 16px;
  color: #6B7280;
  line-height: 1.6;
  margin-bottom: 16px;
}

.library-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.library-size {
  background: #10B981;
  color: white;
  padding: 6px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 600;
}

.library-encoding {
  font-size: 12px;
  color: #9CA3AF;
  background: #F3F4F6;
  padding: 4px 8px;
  border-radius: 4px;
}

/* 节点主理人管理 */
.node-manager-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #EBF5FF 0%, #F0F9FF 50%, #FFF7ED 100%);
  position: relative;
}

.manager-header {
  text-align: center;
  margin-bottom: 60px;
}

.manager-title {
  font-size: 48px;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 16px;
}

.manager-title-gradient {
  background: linear-gradient(135deg, #4682B4 0%, #2E5CE6 50%, #FF6B35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.manager-subtitle {
  font-size: 20px;
  color: #6B7280;
  font-weight: 500;
}

.reliability-badge {
  display: inline-block;
  background: linear-gradient(135deg, #2E5CE6 0%, #FF6B35 100%);
  color: white;
  padding: 6px 20px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 700;
  margin-left: 12px;
}

/* 统计数据 */
.stats-container {
  max-width: 1200px;
  margin: 0 auto 80px;
  padding: 0 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
}

.stat-card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  text-align: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: hidden;
}

.stat-card::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #4682B4 0%, #2E5CE6 50%, #FF6B35 100%);
  transform: scaleX(0);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

.stat-card:hover::after {
  transform: scaleX(1);
}

.stat-number {
  font-size: 48px;
  font-weight: 800;
  background: linear-gradient(135deg, #4682B4 0%, #2E5CE6 50%, #FF6B35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
  margin-bottom: 8px;
}

.stat-label {
  color: #6B7280;
  font-size: 16px;
  font-weight: 500;
}

/* 管理员网格 */
.manager-container {
  max-width: 1400px;
  margin: 0 auto 80px;
  padding: 0 20px;
}

.manager-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
}

.manager-card {
  background: white;
  border-radius: 24px;
  padding: 32px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #E5E7EB;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

.manager-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

.manager-node-icon {
  width: 72px;
  height: 72px;
  background: linear-gradient(135deg, #2E5CE6 0%, #FF6B35 100%);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 36px;
  margin-bottom: 24px;
  box-shadow: 0 10px 20px rgba(46, 92, 230, 0.2);
}

.manager-node-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  color: #111827;
}

.manager-name {
  font-size: 16px;
  color: #6B7280;
  margin-bottom: 16px;
}

.manager-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #10B981;
  color: white;
  padding: 6px 12px;
  border-radius: 100px;
  font-size: 12px;
  font-weight: 600;
}

/* 模型信息部分 */
.model-section {
  padding: 80px 0;
  background: linear-gradient(135deg, #EBF5FF 0%, #F0F9FF 50%, #FFF7ED 100%);
}

.model-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.model-content {
  background: white;
  border-radius: 32px;
  padding: 48px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 48px;
  align-items: center;
}

.model-info {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.model-info-title {
  font-size: 32px;
  font-weight: 800;
  background: linear-gradient(135deg, #4682B4 0%, #2E5CE6 50%, #FF6B35 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.info-item {
  background: #F9FAFB;
  padding: 20px;
  border-radius: 16px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.info-item:hover {
  background: white;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.info-label {
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
  margin-bottom: 8px;
}

.info-value {
  font-size: 18px;
  color: #111827;
  font-weight: 700;
}

.model-visual {
  text-align: center;
}

.model-diagram {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, #2E5CE6 0%, #FF6B35 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 64px;
  color: white;
  box-shadow: 0 20px 40px rgba(46, 92, 230, 0.3);
  animation: float 8s ease-in-out infinite;
}

.model-desc {
  font-size: 16px;
  color: #6B7280;
  line-height: 1.6;
}

/* 浮动元素 */
.floating-elements {
  position: fixed;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: -1;
  overflow: hidden;
}

.floating-element {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, #2E5CE6 0%, #FF6B35 100%);
  opacity: 0.02;
  animation: float-element 20s infinite linear;
}

@keyframes float-element {
  0% {
    transform: translateY(100vh) rotate(0deg);
  }
  100% {
    transform: translateY(-100px) rotate(360deg);
  }
}

/* 响应式设计 */
@media (max-width: 1024px) {
  .hero-title {
    font-size: 48px;
  }

  .manager-title {
    font-size: 36px;
  }

  .model-content {
    grid-template-columns: 1fr;
    gap: 32px;
  }
}

@media (max-width: 768px) {
  .hero-title {
    font-size: 36px;
  }

  .manager-title {
    font-size: 32px;
  }

  .stats-grid,
  .library-grid,
  .manager-grid {
    grid-template-columns: 1fr;
  }

  .graph-container {
    padding: 20px;
  }

  .knowledge-graph {
    height: 400px;
  }

  .graph-node {
    width: 80px;
    height: 80px;
    font-size: 10px;
  }

  .node-icon {
    font-size: 20px;
  }

  /* 连接线在移动端的调整 */
  .graph-connection {
    height: 2px;
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: 28px;
  }

  .hero-subtitle {
    font-size: 16px;
  }

  .quick-actions-grid {
    grid-template-columns: 1fr;
  }

  .knowledge-graph {
    height: 350px;
  }

  .graph-node {
    width: 60px;
    height: 60px;
    font-size: 9px;
  }

  .node-icon {
    font-size: 18px;
    margin-bottom: 2px;
  }
}
</style>